import { useEffect, useMemo, useState } from "react";
import type {
  ActiveProjects,
  DashboardState,
  Escalations,
  Feedback,
  HonestyAudit,
  Meta,
  MissionStrip,
  PhaseProgress,
  Revenue,
  SubOrchestrators,
  SubstrateTransparency,
  TicketThroughput,
  XEngagement,
} from "./types";

const STATE_URL = "/dashboard-state.json";
const FALLBACK_REFRESH_SECONDS = 60;

export default function App() {
  const [state, setState] = useState<DashboardState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const url = `${STATE_URL}?t=${Date.now()}`;
        const res = await fetch(url, { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const payload: DashboardState = await res.json();
        if (cancelled) return;
        setState(payload);
        setLoadedAt(new Date());
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      }
    };
    load();
    const refreshSec =
      state?.meta?.auto_refresh_seconds ?? FALLBACK_REFRESH_SECONDS;
    const id = window.setInterval(load, Math.max(15, refreshSec) * 1000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!state && !error) {
    return (
      <div className="app">
        <div className="app-loading">
          <span>loading dashboard-state.json…</span>
        </div>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="app">
        <div className="app-error">
          <span className="heading">dashboard-state.json failed to load</span>
          <span>{error}</span>
          <span className="panel-cite">
            run `python3 scripts/dashboard_snapshot.py` to regenerate.
          </span>
        </div>
      </div>
    );
  }

  const { panels, meta } = state;

  return (
    <div className="app">
      <Header />
      <main className="app-main">
        <Mission data={panels.mission_strip} />
        <Phase data={panels.phase_progress} />
        <Throughput data={panels.ticket_throughput} />
        <Projects data={panels.active_projects} />
        <SubOrchs data={panels.sub_orchestrators} />
        <EscalationsPanel data={panels.escalations} />
        <RevenuePanel data={panels.revenue} />
        <XPanel data={panels.x_engagement} />
        <SubstratePanel data={panels.substrate_transparency} />
        <AuditPanel data={panels.honesty_audit} />
      </main>
      <Footer
        meta={meta}
        feedback={panels.feedback}
        loadedAt={loadedAt}
        snapshotError={error}
      />
    </div>
  );
}

// ---------- header ----------

function Header() {
  return (
    <header className="app-header">
      <div className="app-header-inner">
        <a className="wordmark" href="/" aria-label="TrillionAI">
          TrillionAI
        </a>
        <nav className="app-nav" aria-label="primary">
          <a href="/">/</a>
          <a className="is-active" href="/live" aria-current="page">
            /live
          </a>
          <a href="https://x.com/TrillionAICorp" rel="noopener external">
            @TrillionAICorp
          </a>
        </nav>
      </div>
    </header>
  );
}

// ---------- 1. mission strip ----------

function Mission({ data }: { data: MissionStrip }) {
  return (
    <section className="panel" aria-labelledby="mission-heading">
      <h1 id="mission-heading" className="mission-headline">
        Building a <em>$1T company</em> autonomously.
      </h1>
      <p className="mission-sub">{data.stewardship}</p>
      <p className="mission-substrate" aria-label="Substrate disclosure">
        {data.substrate.map((row) => (
          <span key={row.label}>
            <span className="label">{row.label}</span>
            {row.value}
          </span>
        ))}
        <span>
          <span className="label">Steward</span>
          Michael Zola
        </span>
      </p>
      <p className="panel-cite">Source · {data.source}</p>
    </section>
  );
}

// ---------- 2. phase progress ----------

function Phase({ data }: { data: PhaseProgress }) {
  const active = data.active;
  const blocking = data.blocking;
  const phaseStrip = useMemo(() => {
    return data.all.map((p) => {
      let cls = "phase";
      if (p.percent_complete >= 100 && p.total_tickets > 0) cls += " is-closed";
      if (p.phase === active.phase) cls += " is-active";
      if (blocking && p.phase === blocking.phase) cls += " is-blocking";
      return { ...p, cls };
    });
  }, [data.all, active.phase, blocking]);

  return (
    <section className="panel" aria-labelledby="phase-heading">
      <span className="eyebrow">Phase progress</span>
      <h2 id="phase-heading" className="panel-heading">
        Phase {active.phase} — {phaseShortLabel(active.label)}
      </h2>

      <div className="focal-row">
        <div className="focal-stat">
          <span className="focal-stat-number">{active.percent_complete}%</span>
          <span className="focal-stat-label">
            phase {active.phase} closed
          </span>
          <span className="focal-stat-detail">
            {active.closed_tickets}/{active.total_tickets} tickets
          </span>
        </div>
        <div className="focal-stat">
          <span className="focal-stat-number">{active.in_progress_tickets}</span>
          <span className="focal-stat-label">in progress · current</span>
          <span className="focal-stat-detail">
            {active.open_tickets} open total
          </span>
        </div>
        {blocking && (
          <div className="focal-aside">
            <span className="dot accent" aria-hidden="true" />
            <div>
              <div className="lead">
                blocking · phase {blocking.phase}
              </div>
              <p className="body">
                {phaseShortLabel(blocking.label)} — {blocking.percent_complete}% closed,{" "}
                {blocking.waiting_tickets} ticket
                {blocking.waiting_tickets === 1 ? "" : "s"} waiting on admin batch.
              </p>
              <p className="meta">Next gate: {data.next_phase_gate}</p>
            </div>
          </div>
        )}
      </div>

      <div className="phase-strip" role="list" aria-label="All phases">
        {phaseStrip.map((p) => (
          <span key={p.phase} className={p.cls} role="listitem">
            <span className="num">{p.phase}</span>
            <span>{phaseShortLabel(p.label, true)}</span>
          </span>
        ))}
      </div>

      <p className="panel-cite">Source · {data.source}</p>
    </section>
  );
}

function phaseShortLabel(label: string, ultraShort = false): string {
  if (!label) return ultraShort ? "—" : "(unlabelled)";
  const cleaned = label.replace(/\s*\([^)]*\)\s*$/, "").trim();
  if (!ultraShort) return cleaned;
  const parts = cleaned.split(/\s+/);
  return parts.slice(0, 3).join(" ");
}

// ---------- 3. ticket throughput ----------

function Throughput({ data }: { data: TicketThroughput }) {
  const topProjects = Object.entries(data.by_project_top).slice(0, 6);
  return (
    <section className="panel" aria-labelledby="throughput-heading">
      <span className="eyebrow">Ticket throughput</span>
      <h2 id="throughput-heading" className="panel-heading">
        What shipped, what's in flight.
      </h2>
      <div className="focal-row">
        <div className="focal-stat">
          <span className="focal-stat-number">{data.last_7d.opened}</span>
          <span className="focal-stat-label">opened · last 7d</span>
        </div>
        <div className="focal-stat">
          <span className="focal-stat-number">{data.last_7d.closed}</span>
          <span className="focal-stat-label">closed · last 7d</span>
        </div>
        <div className="focal-stat">
          <span className="focal-stat-number">{data.last_7d.in_progress}</span>
          <span className="focal-stat-label">in progress · current</span>
        </div>
        <div className="focal-stat">
          <span className="focal-stat-number">{data.total_tickets}</span>
          <span className="focal-stat-label">total tickets · all time</span>
        </div>
      </div>

      <div className="table-scroll">
        <table className="table" aria-label="Top projects by open ticket count">
          <thead>
            <tr>
              <th>Project</th>
              <th className="right">Tickets (lifetime)</th>
            </tr>
          </thead>
          <tbody>
            {topProjects.map(([slug, count]) => (
              <tr key={slug}>
                <td className="project-cell">{slug}</td>
                <td className="right">{count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="panel-cite">
        Last 30d · opened {data.last_30d.opened} · closed {data.last_30d.closed} ·{" "}
        Source · {data.source}
      </p>
    </section>
  );
}

// ---------- 4. active projects ----------

function Projects({ data }: { data: ActiveProjects }) {
  const rows = data.rows.slice(0, 8);
  return (
    <section className="panel" aria-labelledby="projects-heading">
      <span className="eyebrow">Active projects</span>
      <h2 id="projects-heading" className="panel-heading">
        The portfolio.
      </h2>
      <div className="focal-row">
        <div className="focal-stat">
          <span className="focal-stat-number">{data.active_count}</span>
          <span className="focal-stat-label">active</span>
        </div>
        <div className="focal-stat">
          <span className="focal-stat-number">{data.complete_count}</span>
          <span className="focal-stat-label">complete</span>
        </div>
        <div className="focal-stat">
          <span className="focal-stat-number">{data.paused_count}</span>
          <span className="focal-stat-label">paused</span>
        </div>
      </div>

      <div className="table-scroll">
        <table className="table">
          <thead>
            <tr>
              <th>Project</th>
              <th>Status</th>
              <th className="right">Open</th>
              <th className="right">Activated</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.slug}>
                <td className="project-cell">{trimTitle(row.title, row.slug)}</td>
                <td>
                  <span className="status-cell">
                    <span className={`dot ${statusDotClass(row.status)}`} aria-hidden="true" />
                    {row.status}
                  </span>
                </td>
                <td className="right">{row.open_tickets}</td>
                <td className="right muted">{shortDate(row.activated)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="panel-cite">Source · {data.source}</p>
    </section>
  );
}

function statusDotClass(status: string): string {
  const s = status.toLowerCase();
  if (s === "active") return "";
  if (s === "complete") return "muted";
  if (s === "paused") return "empty";
  if (s === "blocked" || s === "waiting") return "accent";
  return "muted";
}

function trimTitle(title: string, slug: string): string {
  if (!title) return slug;
  const dash = title.indexOf(" — ");
  return dash > 0 ? title.slice(0, dash) : title;
}

function shortDate(iso: string): string {
  if (!iso) return "—";
  const t = iso.replace(" ", "T").slice(0, 16);
  return t || "—";
}

// ---------- 5. sub-orchestrators ----------

function SubOrchs({ data }: { data: SubOrchestrators }) {
  return (
    <section className="panel" aria-labelledby="orch-heading">
      <span className="eyebrow">Sub-orchestrators</span>
      <h2 id="orch-heading" className="panel-heading">
        Autonomous workforce.
      </h2>
      <div className="focal-row">
        <div className="focal-stat">
          <span className="focal-stat-number">{data.active_count}</span>
          <span className="focal-stat-label">active</span>
        </div>
        <div className="focal-stat">
          <span className="focal-stat-number">{data.paused_count}</span>
          <span className="focal-stat-label">paused</span>
        </div>
      </div>

      <div className="table-scroll">
        <table className="table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Function</th>
              <th>Parent</th>
              <th>Lifecycle</th>
              <th className="right">Cadence</th>
              <th className="right">Last heartbeat</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => (
              <tr key={row.id}>
                <td className="id-cell">{row.id}</td>
                <td>{row.function}</td>
                <td className="muted">{row.parent}</td>
                <td>
                  <span className="status-cell">
                    <span
                      className={`dot ${row.lifecycle === "active" ? "" : row.lifecycle === "paused" ? "empty" : "muted"}`}
                      aria-hidden="true"
                    />
                    {row.lifecycle}
                  </span>
                </td>
                <td className="right muted">
                  {row.cadence_minutes != null ? `${row.cadence_minutes} min` : "—"}
                </td>
                <td className="right">{shortDate(row.last_heartbeat ?? "")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="panel-cite">Source · {data.source}</p>
    </section>
  );
}

// ---------- 6. escalations ----------

function EscalationsPanel({ data }: { data: Escalations }) {
  return (
    <section className="panel" aria-labelledby="escalation-heading">
      <span className="eyebrow">Escalations</span>
      <h2 id="escalation-heading" className="panel-heading">
        What's blocking the platform.
      </h2>

      <div className="focal-row">
        <div className="focal-stat">
          <span className="focal-stat-number">{data.open_count}</span>
          <span className="focal-stat-label">open escalations</span>
          <span className="focal-stat-detail">
            {data.urgent_count} urgent · {data.open_count - data.urgent_count} routine
          </span>
        </div>
        <div className="focal-stat">
          <span className="focal-stat-number">{data.waiting_tickets_count}</span>
          <span className="focal-stat-label">tickets · waiting | blocked</span>
          {data.oldest_unresolved && (
            <span className="focal-stat-detail">
              oldest: {data.oldest_unresolved.id} · updated {shortDate(data.oldest_unresolved.updated)}
            </span>
          )}
        </div>
      </div>

      {data.items.length > 0 && (
        <ul className="escalation-list" aria-label="Open escalations">
          {data.items.map((item) => {
            const severity = item.severity || "routine";
            return (
              <li key={item.name} className="escalation">
                <span
                  className={`dot ${severity === "urgent" ? "accent" : ""}`}
                  aria-hidden="true"
                />
                <div className="body">
                  <div className="head">
                    <span className={`severity-tag ${severity === "urgent" ? "urgent" : ""}`}>
                      {severity.toUpperCase()}
                    </span>{" "}
                    · {shortDate(item.created)} · {humanReason(item.reason)}
                    {item.ticket ? ` · ${item.ticket}` : ""}
                  </div>
                  <p className="meta">Source · {item.source}</p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <p className="panel-cite">Source · {data.source}</p>
    </section>
  );
}

function humanReason(reason: string): string {
  if (!reason) return "—";
  return reason.replace(/_/g, " ");
}

// ---------- 7. revenue / runway ----------

function RevenuePanel({ data }: { data: Revenue }) {
  return (
    <section className="panel" aria-labelledby="rev-heading">
      <span className="eyebrow">Revenue · runway</span>
      <h2 id="rev-heading" className="panel-heading">
        MRR · customers · runway.
      </h2>
      <div className="focal-row">
        <div className="focal-stat">
          <span className="focal-stat-number lg">${data.mrr_usd}</span>
          <span className="focal-stat-label">{data.status_label}</span>
          <span className="focal-stat-detail">
            currency · {data.currency}
          </span>
        </div>
      </div>
      <p style={{ margin: 0, color: "var(--ink)", maxWidth: "60ch" }}>{data.explanation}</p>
      <p className="panel-cite">Source · {data.source}</p>
    </section>
  );
}

// ---------- 8. x engagement ----------

function XPanel({ data }: { data: XEngagement }) {
  const inaugural = data.inaugural_post;
  return (
    <section className="panel" aria-labelledby="x-heading">
      <span className="eyebrow">X engagement</span>
      <h2 id="x-heading" className="panel-heading">
        {data.handle}
      </h2>
      {inaugural?.tweet_url && (
        <p style={{ margin: 0 }}>
          <a className="x-link" href={inaugural.tweet_url} rel="noopener external">
            inaugural post · pinned {inaugural.pinned_at ?? "—"} · {inaugural.tweet_url}
          </a>
        </p>
      )}
      <div className="kv-grid">
        <div className="kv">
          <div className="label">followers</div>
          <div className={`value ${data.metrics.followers == null ? "pending" : ""}`}>
            {data.metrics.followers ?? "pending api ingest"}
          </div>
        </div>
        <div className="kv">
          <div className="label">engagement 30d</div>
          <div className={`value ${data.metrics.engagement_rate_30d_pct == null ? "pending" : ""}`}>
            {data.metrics.engagement_rate_30d_pct == null
              ? "pending api ingest"
              : `${data.metrics.engagement_rate_30d_pct}%`}
          </div>
        </div>
        <div className="kv">
          <div className="label">posts last 7d</div>
          <div className={`value ${data.metrics.posts_last_7d == null ? "pending" : ""}`}>
            {data.metrics.posts_last_7d ?? "pending api ingest"}
          </div>
        </div>
        <div className="kv">
          <div className="label">last posted</div>
          <div className={`value ${!data.last_posted_at ? "pending" : ""}`}>
            {data.last_posted_at ?? "—"}
          </div>
        </div>
      </div>
      <p className="panel-cite">Source · {data.source}</p>
    </section>
  );
}

// ---------- 9. substrate transparency ----------

function SubstratePanel({ data }: { data: SubstrateTransparency }) {
  return (
    <section className="panel" aria-labelledby="substrate-heading">
      <span className="eyebrow">Substrate transparency</span>
      <h2 id="substrate-heading" className="panel-heading">
        What runs this company.
      </h2>

      <div className="split-grid">
        <div>
          <div className="subhead">Models</div>
          <table className="table">
            <tbody>
              {data.vendors.map((v) => (
                <tr key={v.role}>
                  <td className="muted" style={{ width: "30%" }}>
                    {v.role.toLowerCase()}
                  </td>
                  <td>{v.vendor} {v.model}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          <div className="subhead">Agent credit pools · month to date</div>
          <table className="table">
            <tbody>
              {data.credit_pools.map((p) => (
                <tr key={p.agent}>
                  <td className="muted" style={{ width: "26%" }}>{p.agent}</td>
                  <td>
                    {p.used_month}/{p.budget_month} · {p.pct_used}
                  </td>
                  <td className="right">
                    <span className="status-cell">
                      <span
                        className={`dot ${p.status === "healthy" ? "" : "empty"}`}
                        aria-hidden="true"
                      />
                      {p.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="spend-summary" style={{ marginTop: 16 }}>
            <div className="spend-row">
              <span className="label">rolling spend · 24h</span>
              <span className="value">
                {data.last_24h_spend_usd != null ? `$${data.last_24h_spend_usd.toFixed(2)}` : "—"}
              </span>
            </div>
            <div className="spend-row">
              <span className="label">rolling spend · 7d</span>
              <span className="value">
                {data.last_7d_spend_usd != null ? `$${data.last_7d_spend_usd.toFixed(2)}` : "—"}
              </span>
            </div>
          </div>
        </div>
      </div>
      <p className="panel-cite">{data.monthly_inference_spend_status}</p>
      <p className="panel-cite">
        Source · {data.source} · audit log · {data.audit_log_link}
      </p>
    </section>
  );
}

// ---------- 10. honesty audit ----------

function AuditPanel({ data }: { data: HonestyAudit }) {
  if (data.status === "missing") {
    return (
      <section className="panel" aria-labelledby="audit-heading">
        <span className="eyebrow">Honesty audit</span>
        <h2 id="audit-heading" className="panel-heading">
          Most recent governance run.
        </h2>
        <p className="panel-cite">{data.message ?? "—"}</p>
      </section>
    );
  }
  const passed = data.passed === true;
  return (
    <section className="panel" aria-labelledby="audit-heading">
      <span className="eyebrow">Honesty audit</span>
      <h2 id="audit-heading" className="panel-heading">
        Most recent governance run.
      </h2>
      <div className={`audit-headline ${passed ? "pass" : "fail"}`}>
        <span className="dot" aria-hidden="true" />
        {passed ? "PASS" : "FAIL"}
      </div>
      <div className="audit-meta">
        <div className="item">
          <div className="label">window</div>
          <div>
            {(data.window_start ?? "—").slice(0, 10)} → {(data.window_end ?? "—").slice(0, 10)}
          </div>
        </div>
        <div className="item">
          <div className="label">failures</div>
          <div>{data.failure_count ?? 0}</div>
        </div>
        <div className="item">
          <div className="label">warnings</div>
          <div>{data.warning_count ?? 0}</div>
        </div>
        <div className="item">
          <div className="label">report</div>
          <div>
            <a href={`https://github.com/`} aria-label="full audit report path">
              {data.report_path}
            </a>
          </div>
        </div>
      </div>
      {data.failures && data.failures.length > 0 && (
        <ul className="audit-failure-list" aria-label="Audit failures">
          {data.failures.map((f, i) => (
            <li key={i}>{f}</li>
          ))}
        </ul>
      )}
      <p className="panel-cite">Source · {data.source}</p>
    </section>
  );
}

// ---------- 11 + 12. footer (last-updated stamp + feedback link) ----------

function Footer({
  meta,
  feedback,
  loadedAt,
  snapshotError,
}: {
  meta: Meta;
  feedback: Feedback;
  loadedAt: Date | null;
  snapshotError: string | null;
}) {
  const refreshSec = meta.auto_refresh_seconds || FALLBACK_REFRESH_SECONDS;
  return (
    <footer className="app-footer">
      <div className="app-footer-inner">
        <div className="meta-cell">
          last updated {fmtTs(meta.generated_at)}
          <br />
          page auto-refreshes every {refreshSec}s · target cadence {meta.refresh_cadence_target}
          {loadedAt && (
            <>
              <br />
              browser fetched {loadedAt.toLocaleTimeString()}
            </>
          )}
          {snapshotError && (
            <>
              <br />
              fetch error: {snapshotError}
            </>
          )}
        </div>
        <div className="meta-cell">
          {feedback.headline}{" "}
          {feedback.body}{" "}
          <a className="feedback-link" href={feedback.primary_link.url} rel="noopener external">
            {feedback.primary_link.label} →
          </a>
        </div>
        <div className="meta-cell right">
          TrillionAI · Delaware, USA
          <br />
          trillionaicorp.com
          <br />
          schema v{meta.schema_version} · snapshot script · {meta.snapshot_script}
        </div>
      </div>
    </footer>
  );
}

function fmtTs(iso: string): string {
  if (!iso) return "—";
  return iso.replace("T", " ");
}
